import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { AutoComplete } from '@/components/ui/autocomplete';
import { Button } from '@/components/ui/button';
import { cn } from '~/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, ChevronDown, ChevronRight, CreditCard, Gift, Package, Percent, ShoppingBag, Sparkles, Tag, Ticket, Trash2, X } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BillingDraftFormData, BillingDraftLineItem } from './BillingDraftCreator';
import { trpc } from '~/api/trpc';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getDiscountCodeWarnings, formatDiscountCodeWarningMessage } from '~/lib/discount-code-warnings';
import { isDiscountValidForStore } from '~/lib/discount-code-store';

type ApplicationCustomerType = 'NEW_CUSTOMERS_ONLY' | 'RETURNING_CUSTOMERS_ONLY' | 'ALL_CUSTOMERS';
type CustomerType = 'NEW' | 'RETURNING';

type ValidDiscountCode = {
    id: string;
    name: string;
    description: string;
    type: string;
    discountValue: number;
    applicationLevel: string;
    applicationCustomerType: ApplicationCustomerType;
    startDate: string | Date;
    endDate: string | Date;
    validDaysOfWeek: number[];
    validTimeWindowStart: string | null;
    validTimeWindowEnd: string | null;
    validBookableItemIds: string[];
    validTreatmentVariantIds: string[];
    validBundleIds: string[];
    validProductIds: string[];
    validStoreIds: number[];
};

export type DiscountDefinition = {
    id: string;
    source: 'code' | 'manual' | 'cart';
    discountCodeId: string | null;
    label: string;
    type: 'ABSOLUTE' | 'PERCENTAGE';
    value: number;
    targetAll: boolean;
    targetIndices: number[];
    validBookableItemIds: string[];
    validTreatmentVariantIds: string[];
    validBundleIds: string[];
    validProductIds: string[];
    note?: string | null;
};

type BillingDraftLineItemsEditorProps = {
    form: UseFormReturn<BillingDraftFormData>;
    totals: {
        totalGross: number;
        totalGrossDiscounted: number;
        totalNet: number;
        totalNetDiscounted: number;
        vatAmount: number;
        vatAmountDiscounted: number;
    };
    validDiscountCodes: ValidDiscountCode[];
    initialDiscountDefinitions: DiscountDefinition[];
    appointmentCouponCodes?: string | null;
    appointmentStartTime?: string | null;
    customerType: CustomerType | null;
    noTreatmentPerformed?: boolean;
    noTreatmentPractitioners?: string[];
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

type SelectedAddOn = {
    addOnId: string;
    addOnName: string;
    variantId: string;
    variantName: string;
    priceCents: number;
};

// Type for available vouchers from the server
type AvailableVoucher = {
    id: string;
    treatmentVariantId: string;
    treatmentVariantName: string;
    unitPriceGross: number;
    unitQuantity: number;
    remainingUnits: number;
    sourceBillingDraftNumber: string;
    createdAt: string;
};

// Sub-component: Inline recipient customer picker for gift card line items
const GiftCardRecipientPicker = ({ index, currentRecipientId, onRecipientChange }: {
    index: number;
    currentRecipientId: number | null | undefined;
    onRecipientChange: (index: number, customerId: number | null) => void;
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const { data: customersData } = trpc.customers.getAll.useQuery(
        { page: 0, pageSize: 10, filters: { name: searchTerm } },
        { enabled: searchTerm.length > 0 }
    );

    const { data: selectedCustomer } = trpc.customers.getOne.useQuery(
        { id: currentRecipientId! },
        { enabled: !!currentRecipientId }
    );

    const recipientName = selectedCustomer?.data
        ? `${selectedCustomer.data.firstName} ${selectedCustomer.data.lastName}`
        : null;

    return (
        <div className='flex items-center gap-1.5 mt-1'>
            <span className='text-xs text-muted-foreground whitespace-nowrap'>Empfänger:</span>
            {currentRecipientId && recipientName ? (
                <div className='flex items-center gap-1'>
                    <Badge variant='outline' className='text-xs gap-1'>
                        {recipientName}
                        <button
                            type='button'
                            onClick={() => onRecipientChange(index, null)}
                            className='hover:text-destructive'
                        >
                            <X className='h-3 w-3' />
                        </button>
                    </Badge>
                </div>
            ) : (
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant='outline'
                            size='sm'
                            className='h-7 text-xs w-48 justify-start font-normal text-muted-foreground'
                        >
                            Kunde suchen...
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-64 p-0' align='start'>
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder='Kunde suchen...'
                                value={searchTerm}
                                onValueChange={setSearchTerm}
                                className='text-xs'
                            />
                            <CommandList className='max-h-40'>
                                {searchTerm.length === 0 && (
                                    <CommandEmpty>Name eingeben...</CommandEmpty>
                                )}
                                {searchTerm.length > 0 && (!customersData?.data || customersData.data.length === 0) && (
                                    <CommandEmpty>Kein Kunde gefunden.</CommandEmpty>
                                )}
                                {searchTerm.length > 0 && customersData?.data && customersData.data.length > 0 && (
                                    <CommandGroup>
                                        {customersData.data.map((customer) => (
                                            <CommandItem
                                                key={customer.id}
                                                value={String(customer.id)}
                                                onSelect={() => {
                                                    onRecipientChange(index, customer.id);
                                                    setSearchTerm('');
                                                    setIsOpen(false);
                                                }}
                                                className='text-xs cursor-pointer'
                                            >
                                                {customer.firstName} {customer.lastName}
                                                {customer.email && (
                                                    <span className='text-muted-foreground ml-1'>({customer.email})</span>
                                                )}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
};

const BillingDraftLineItemsEditor = ({ form, totals, validDiscountCodes, initialDiscountDefinitions, appointmentCouponCodes, appointmentStartTime, customerType, noTreatmentPerformed, noTreatmentPractitioners }: BillingDraftLineItemsEditorProps) => {
    const { fields, append, remove, update } = useFieldArray({
        control: form.control,
        name: 'lineItems',
    });

    const billingDraftType = form.watch('type');
    const selectedAppointmentId = form.watch('appointmentId');
    const isServicesDisabled = !selectedAppointmentId;
    const isBundlesAndGiftCardsDisabled = billingDraftType === 'INFLUENCER' || billingDraftType === 'MARKETING' || billingDraftType === 'ACADEMY' || billingDraftType === 'KULANZ';
    const isProductsDisabled = billingDraftType === 'ACADEMY';
    const isDiscountsDisabled = billingDraftType === 'INFLUENCER' || billingDraftType === 'MARKETING' || billingDraftType === 'KULANZ';

    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    
    // Search states for each category
    const [serviceSearchValue, setServiceSearchValue] = useState('');
    const [serviceSelectedValue, setServiceSelectedValue] = useState('');
    const [bundleSearchValue, setBundleSearchValue] = useState('');
    const [bundleSelectedValue, setBundleSelectedValue] = useState('');
    const [productSearchValue, setProductSearchValue] = useState('');
    const [productSelectedValue, setProductSelectedValue] = useState('');
    const [giftCardSearchValue, setGiftCardSearchValue] = useState('');
    const [giftCardSelectedValue, setGiftCardSelectedValue] = useState('');

    // Gift card amount dialog state
    const [giftCardAmountDialogOpen, setGiftCardAmountDialogOpen] = useState(false);
    const [pendingGiftCardTemplate, setPendingGiftCardTemplate] = useState<any>(null);
    const [giftCardCustomAmount, setGiftCardCustomAmount] = useState('');

    // Variant/Add-on selection dialog state (for services)
    const [variantDialogOpen, setVariantDialogOpen] = useState(false);
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
    const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);

    // Get the selected storeId and customerId from the form
    const selectedStoreId = form.watch('storeId');
    const selectedCustomerId = form.watch('customerId');

    // Fetch available bundle vouchers for the customer
    const { data: availableVouchers } = trpc.billingDrafts.getAvailableVouchersForCustomer.useQuery(
        { customerId: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    // Fetch influencer bundle price overrides (only when type is INFLUENCER)
    const isInfluencer = billingDraftType === 'INFLUENCER';
    const { data: influencerPriceOverrides } = trpc.billingDrafts.getInfluencerPriceOverrides.useQuery(
        { storeId: selectedStoreId },
        { enabled: isInfluencer && selectedStoreId > 0 }
    );

    // Build a map of treatmentVariantId -> available vouchers (accounting for already-used vouchers in current line items)
    const currentLineItems = form.watch('lineItems');
    const vouchersByVariant = useMemo(() => {
        const map = new Map<string, AvailableVoucher[]>();
        if (!availableVouchers) { return map; }

        // Count how many units are already claimed by current line items in the form
        const claimedUnits = new Map<string, number>();
        for (const li of currentLineItems) {
            if (li.paidByBundleVoucherId) {
                const current = claimedUnits.get(li.paidByBundleVoucherId) || 0;
                claimedUnits.set(li.paidByBundleVoucherId, current + 1);
            }
        }

        for (const voucher of availableVouchers) {
            const claimed = claimedUnits.get(voucher.id) || 0;
            const effectiveRemaining = voucher.remainingUnits - claimed;
            if (effectiveRemaining <= 0) { continue; }

            const adjustedVoucher = { ...voucher, remainingUnits: effectiveRemaining };
            const existing = map.get(voucher.treatmentVariantId) || [];
            existing.push(adjustedVoucher);
            map.set(voucher.treatmentVariantId, existing);
        }

        return map;
    }, [availableVouchers, currentLineItems]);

    // Fetch services for billing (bookable items + standalone treatment variants)
    const serviceSearchQuery = trpc.appointments.searchServicesForBilling.useQuery({
        storeId: selectedStoreId > 0 ? selectedStoreId : undefined,
        search: serviceSearchValue,
    }, {
        enabled: selectedStoreId > 0,
    });

    const bundleBookableItemsQuery = trpc.appointments.searchBookableItems.useQuery({
        storeId: selectedStoreId > 0 ? selectedStoreId : undefined,
        search: bundleSearchValue,
    }, {
        enabled: selectedStoreId > 0,
    });

    type BookableItem = NonNullable<typeof serviceSearchQuery.data>['bookableItems'][number];
    const [selectedBookableItem, setSelectedBookableItem] = useState<BookableItem | null>(null);

    // Bookable items (SERVICE type, already filtered by new endpoint)
    const serviceItems = useMemo(() => 
        serviceSearchQuery.data?.bookableItems ?? [],
        [serviceSearchQuery.data]
    );

    // Standalone treatment variants (not linked to any bookable item)
    const standaloneVariantItems = useMemo(() =>
        serviceSearchQuery.data?.standaloneVariants ?? [],
        [serviceSearchQuery.data]
    );

    const bundleItems = useMemo(() => 
        (bundleBookableItemsQuery.data ?? []).filter(item => item.type === 'BUNDLE'),
        [bundleBookableItemsQuery.data]
    );

    // Build a Set of treatment variant IDs that have bundles available for purchase
    const variantsWithBundlesAvailable = useMemo(() => {
        const variantIds = new Set<string>();
        for (const item of bundleItems) {
            if (item.bundle?.treatmentVariant?.id) {
                variantIds.add(item.bundle.treatmentVariant.id);
            }
        }
        return variantIds;
    }, [bundleItems]);

    // --- Discount Management (Definition-based architecture) ---
    // All discounts are tracked as definitions. A centralized recalculation function
    // recomputes line item discount amounts whenever anything changes.
    const [discountDefinitions, setDiscountDefinitions] = useState<DiscountDefinition[]>([]);
    const discountDefsRef = useRef<DiscountDefinition[]>([]);
    discountDefsRef.current = discountDefinitions;

    const [manualDiscountType, setManualDiscountType] = useState<'ABSOLUTE' | 'PERCENTAGE'>('ABSOLUTE');
    const [manualDiscountValue, setManualDiscountValue] = useState('');
    const [manualDiscountNote, setManualDiscountNote] = useState('');
    const [manualDiscountTargetAll, setManualDiscountTargetAll] = useState(true);
    const [manualDiscountTargetIndices, setManualDiscountTargetIndices] = useState<number[]>([]);
    const [showManualDiscountForm, setShowManualDiscountForm] = useState(false);
    const [discountCodeSearchValue, setDiscountCodeSearchValue] = useState('');
    const [discountCodeSelectedValue, setDiscountCodeSelectedValue] = useState('');

    // Customer type mismatch override state
    const [discountWarningDialogOpen, setDiscountWarningDialogOpen] = useState(false);
    const [discountWarningMessage, setDiscountWarningMessage] = useState('');
    const [pendingDiscountCodeId, setPendingDiscountCodeId] = useState<string | null>(null);
    const [overriddenDiscountCodeIds, setOverriddenDiscountCodeIds] = useState<Set<string>>(new Set());

    // Core recalculation: recompute all line item discount amounts from base prices
    const recalculateAllDiscounts = useCallback((definitions: DiscountDefinition[]) => {
        const items = form.getValues('lineItems');
        if (items.length === 0) { return; }

        // Step 1: Base price for each item is totalGross (the original undiscounted price)
        const basePrices = items.map((item: any) => item.totalGross);

        // Step 2: Build new discount entries per item from definitions
        // Split into percentage and absolute definitions. Percentage discounts are applied
        // first (one per item — the highest applicable), then absolute discounts distribute.
        const percentageDefs = definitions.filter(d => d.type === 'PERCENTAGE');
        const absoluteDefs = definitions.filter(d => d.type === 'ABSOLUTE');
        const itemDiscounts: Array<Array<{ discountCodeId: string | null; label: string; discountAmountEur: number; note?: string | null }>> = items.map(() => []);

        // Phase 1: Percentage discounts — each line item gets at most ONE (the highest applicable)
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.paidByBundleVoucherId || item.giftCardTemplateId) { continue; }

            let bestDef: DiscountDefinition | null = null;
            for (const def of percentageDefs) {
                if (!def.targetAll && !def.targetIndices.includes(i)) { continue; }
                const hasRestrictions = def.validBookableItemIds.length > 0 || def.validBundleIds.length > 0 || def.validProductIds.length > 0;
                if (hasRestrictions) {
                    const matchesBookable = item.bookableItemId && def.validBookableItemIds.includes(item.bookableItemId);
                    const matchesVariant = !item.bookableItemId && item.treatmentVariantId
                        && def.validTreatmentVariantIds.includes(item.treatmentVariantId);
                    const matchesBundle = item.bundleId && def.validBundleIds.includes(item.bundleId);
                    const matchesProduct = item.physicalProductId && def.validProductIds.includes(item.physicalProductId);
                    if (!matchesBookable && !matchesVariant && !matchesBundle && !matchesProduct) { continue; }
                }
                if (!bestDef || def.value > bestDef.value) {
                    bestDef = def;
                }
            }

            if (bestDef) {
                const base = basePrices[i];
                let amount = Math.round(base * (bestDef.value / 100) * 100) / 100;
                amount = Math.max(amount, 0);
                if (amount > 0) {
                    itemDiscounts[i].push({ discountCodeId: bestDef.discountCodeId, label: bestDef.label, discountAmountEur: amount, note: bestDef.note });
                }
            }
        }

        // Phase 2: Absolute discounts — distributed proportionally
        for (const def of absoluteDefs) {
            const applicable: number[] = [];
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (!def.targetAll && !def.targetIndices.includes(i)) { continue; }
                const hasRestrictions = def.validBookableItemIds.length > 0 || def.validBundleIds.length > 0 || def.validProductIds.length > 0;
                if (hasRestrictions) {
                    const matchesBookable = item.bookableItemId && def.validBookableItemIds.includes(item.bookableItemId);
                    const matchesVariant = !item.bookableItemId && item.treatmentVariantId
                        && def.validTreatmentVariantIds.includes(item.treatmentVariantId);
                    const matchesBundle = item.bundleId && def.validBundleIds.includes(item.bundleId);
                    const matchesProduct = item.physicalProductId && def.validProductIds.includes(item.physicalProductId);
                    if (!matchesBookable && !matchesVariant && !matchesBundle && !matchesProduct) { continue; }
                }
                applicable.push(i);
            }
            if (applicable.length === 0) { continue; }

            const applicableActive = applicable.filter(i =>
                !items[i].paidByBundleVoucherId
                && !items[i].giftCardTemplateId
            );

            const totalBase = applicableActive.reduce((s, i) => s + basePrices[i], 0);
            if (totalBase <= 0) { continue; }

            let distributed = 0;
            for (let idx = 0; idx < applicableActive.length; idx++) {
                const i = applicableActive[idx];
                const isLast = idx === applicableActive.length - 1;
                let amount: number;
                if (isLast) {
                    amount = Math.round((def.value - distributed) * 100) / 100;
                } else {
                    amount = Math.round((basePrices[i] / totalBase) * def.value * 100) / 100;
                }
                // Cap: can't discount more than remaining base after earlier discounts
                const existingForItem = itemDiscounts[i].reduce((s, d) => s + d.discountAmountEur, 0);
                amount = Math.min(amount, basePrices[i] - existingForItem);
                amount = Math.max(amount, 0);
                distributed += amount;
                if (amount <= 0) { continue; }
                itemDiscounts[i].push({ discountCodeId: def.discountCodeId, label: def.label, discountAmountEur: amount, note: def.note });
            }
        }

        // Step 3: Write back updated discounts and recalculated discounted prices
        // Note: totalGross, totalNet, vatAmount (original values) are NOT overwritten
        for (let i = 0; i < items.length; i++) {
            const grossDiscountAmount = itemDiscounts[i].reduce((s, d) => s + d.discountAmountEur, 0);
            const totalGrossDiscounted = Math.max(basePrices[i] - grossDiscountAmount, 0);
            const vatRate = items[i].vatRate;
            const totalNetDiscounted = totalGrossDiscounted / (1 + vatRate);
            const vatAmountDiscounted = totalGrossDiscounted - totalNetDiscounted;
            const netDiscountAmount = grossDiscountAmount / (1 + vatRate);
            form.setValue(`lineItems.${i}.discounts`, itemDiscounts[i]);
            form.setValue(`lineItems.${i}.grossDiscountAmount`, grossDiscountAmount);
            form.setValue(`lineItems.${i}.totalGrossDiscounted`, totalGrossDiscounted);
            form.setValue(`lineItems.${i}.netDiscountAmount`, netDiscountAmount);
            form.setValue(`lineItems.${i}.totalNetDiscounted`, totalNetDiscounted);
            form.setValue(`lineItems.${i}.vatAmountDiscounted`, vatAmountDiscounted);
        }
    }, [form]);

    // Clear discount definitions when switching to a type that disallows discounts
    useEffect(() => {
        if (isDiscountsDisabled && discountDefinitions.length > 0) {
            setDiscountDefinitions([]);
            recalculateAllDiscounts([]);
        }
    }, [isDiscountsDisabled]);

    // Initialize from cart-loaded discount definitions
    const initialDefsAppliedRef = useRef(false);
    useEffect(() => {
        if (!initialDefsAppliedRef.current && initialDiscountDefinitions.length > 0 && currentLineItems.length > 0 && !isDiscountsDisabled) {
            initialDefsAppliedRef.current = true;
            setDiscountDefinitions(initialDiscountDefinitions);
            recalculateAllDiscounts(initialDiscountDefinitions);
        }
    }, [initialDiscountDefinitions, currentLineItems.length, recalculateAllDiscounts, isDiscountsDisabled]);

    const applyDiscountCodeDirectly = useCallback((codeId: string, isOverride = false) => {
        const code = validDiscountCodes.find(c => c.id === codeId);
        if (!code) { return; }
        const currentDefs = discountDefsRef.current;
        if (currentDefs.some(d => d.discountCodeId === codeId)) { return; }

        const newDef: DiscountDefinition = {
            id: `code-${codeId}`,
            source: 'code',
            discountCodeId: codeId,
            label: code.name,
            type: code.type === 'PERCENTAGE_DISCOUNT' ? 'PERCENTAGE' : 'ABSOLUTE',
            value: code.discountValue,
            targetAll: true,
            targetIndices: [],
            validBookableItemIds: code.validBookableItemIds,
            validTreatmentVariantIds: code.validTreatmentVariantIds,
            validBundleIds: code.validBundleIds,
            validProductIds: code.validProductIds,
        };
        const newDefs = [...currentDefs, newDef];
        setDiscountDefinitions(newDefs);
        recalculateAllDiscounts(newDefs);
        setDiscountCodeSearchValue('');

        if (isOverride) {
            setOverriddenDiscountCodeIds(prev => new Set([...prev, codeId]));
        }
    }, [validDiscountCodes, recalculateAllDiscounts]);

    // Apply a discount code: check all conditions and show a single warning dialog if any fail
    const handleApplyDiscountCode = useCallback((codeId: string) => {
        const code = validDiscountCodes.find(c => c.id === codeId);
        if (!code) { return; }
        if (discountDefsRef.current.some(d => d.discountCodeId === codeId)) { return; }

        const warnings = getDiscountCodeWarnings(code, appointmentStartTime, customerType);
        if (warnings.length > 0) {
            setDiscountWarningMessage(formatDiscountCodeWarningMessage(code.name, warnings));
            setPendingDiscountCodeId(codeId);
            setDiscountWarningDialogOpen(true);
            return;
        }

        applyDiscountCodeDirectly(codeId);
    }, [validDiscountCodes, customerType, appointmentStartTime, applyDiscountCodeDirectly]);

    // Remove any discount definition by ID and recalculate
    const handleRemoveDiscount = useCallback((defId: string) => {
        const newDefs = discountDefsRef.current.filter(d => d.id !== defId);
        setDiscountDefinitions(newDefs);
        recalculateAllDiscounts(newDefs);
    }, [recalculateAllDiscounts]);

    // Apply a manual discount: create a definition and recalculate
    const handleApplyManualDiscount = useCallback(() => {
        const value = parseFloat(manualDiscountValue);
        if (isNaN(value) || value <= 0) { return; }
        if (!manualDiscountNote.trim()) { return; }

        const items = form.getValues('lineItems');
        const targetIndices = manualDiscountTargetAll
            ? items.map((_: any, i: number) => i)
            : manualDiscountTargetIndices;
        if (targetIndices.length === 0) { return; }

        const newDef: DiscountDefinition = {
            id: `manual-${Date.now()}`,
            source: 'manual',
            discountCodeId: null,
            label: `Manueller Rabatt (${manualDiscountType === 'PERCENTAGE' ? `${value}%` : formatCurrency(value)})`,
            type: manualDiscountType,
            value,
            targetAll: manualDiscountTargetAll,
            targetIndices: manualDiscountTargetAll ? [] : [...targetIndices],
            validBookableItemIds: [],
            validTreatmentVariantIds: [],
            validBundleIds: [],
            validProductIds: [],
            note: manualDiscountNote.trim(),
        };
        const currentDefs = discountDefsRef.current;
        const newDefs = [...currentDefs, newDef];
        setDiscountDefinitions(newDefs);
        recalculateAllDiscounts(newDefs);
        setManualDiscountValue('');
        setManualDiscountNote('');
        setManualDiscountTargetIndices([]);
    }, [form, manualDiscountType, manualDiscountValue, manualDiscountNote, manualDiscountTargetAll, manualDiscountTargetIndices, recalculateAllDiscounts]);

    // Derived values
    const appliedDiscountCodeIds = discountDefinitions
        .filter(d => d.discountCodeId)
        .map(d => d.discountCodeId!);

    const totalDiscount = currentLineItems.reduce((sum, item) => {
        return sum + (item.discounts || []).reduce((dSum, d) => dSum + d.discountAmountEur, 0);
    }, 0);

    const availableDiscountCodes = validDiscountCodes.filter(c =>
        !appliedDiscountCodeIds.includes(c.id)
        // Hard-filter out codes scoped to stores that do not include the draft's store.
        && isDiscountValidForStore(c.validStoreIds, selectedStoreId > 0 ? selectedStoreId : null)
    );

    const filteredDiscountCodes = (() => {
        if (!discountCodeSearchValue) { return availableDiscountCodes; }
        const search = discountCodeSearchValue.toLowerCase();
        return availableDiscountCodes.filter(c =>
            c.name.toLowerCase().includes(search) || c.description.toLowerCase().includes(search)
        );
    })();

    // Build autocomplete options for discount codes
    const discountCodeOptions = useMemo(() => {
        return filteredDiscountCodes.map((code) => ({
            label: code.name,
            value: code.id,
            renderItem: (
                <div className='w-full flex justify-between items-center'>
                    <div className='flex flex-col'>
                        {code.description && (
                            <span className='text-xs text-muted-foreground'>{code.description}</span>
                        )}
                        <span>{code.name}</span>
                    </div>
                    <span className='font-mono text-sm text-muted-foreground ml-4 shrink-0'>
                        {code.type === 'PERCENTAGE_DISCOUNT'
                            ? `${code.discountValue}%`
                            : formatCurrency(code.discountValue)}
                    </span>
                </div>
            ),
        }));
    }, [filteredDiscountCodes]);

    // Build autocomplete options for services (bookable items first, standalone variants after)
    const serviceItemOptions = useMemo(() => {
        const bookableItemOptions = serviceItems.map((item) => {
            const getVariantPrice = (v: { priceCents: number; id: string }) => {
                if (isInfluencer && influencerPriceOverrides?.[v.id] !== undefined) {
                    return influencerPriceOverrides[v.id];
                }
                return v.priceCents / 100;
            };

            const priceDisplay = item.variantOptions.length === 1 && item.variantOptions[0]?.treatmentVariant
                ? formatCurrency(getVariantPrice(item.variantOptions[0].treatmentVariant))
                : item.variantOptions.length > 1
                    ? `ab ${formatCurrency(Math.min(...item.variantOptions.filter(v => v.treatmentVariant).map(v => getVariantPrice(v.treatmentVariant!))))}`
                    : '';

            const hasBundleAvailable = item.variantOptions.some(
                v => v.treatmentVariant && variantsWithBundlesAvailable.has(v.treatmentVariant.id)
            );

            return {
                label: item.nameDe,
                value: `bi_${item.id}`,
                renderItem: (
                    <div className='w-full flex justify-between items-center'>
                        <div className='flex flex-col'>
                            <span className='text-xs text-muted-foreground'>
                                {item.variantOptions.length > 1 && `${item.variantOptions.length} Varianten`}
                                {item.variantOptions.length > 1 && item.addOns.length > 0 && ' • '}
                                {item.addOns.length > 0 && `${item.addOns.length} Add-Ons`}
                            </span>
                            <div className='flex items-center gap-2'>
                                <span>{item.nameDe}</span>
                                {hasBundleAvailable && (
                                    <Badge variant='default' className='text-[10px] px-1.5 py-0 bg-blue-600'>
                                        <Package className='h-3 w-3 mr-0.5' />
                                        Bundle
                                    </Badge>
                                )}
                            </div>
                        </div>
                        {priceDisplay && (
                            <span className='font-mono text-sm text-muted-foreground ml-4 shrink-0'>
                                {priceDisplay}
                            </span>
                        )}
                    </div>
                ),
            };
        });

        const standaloneOptions = standaloneVariantItems.map((v) => ({
            label: v.name,
            value: `sv_${v.id}`,
            renderItem: (
                <div className='w-full flex justify-between items-center'>
                    <div className='flex flex-col'>
                        <span className='text-xs text-muted-foreground'>{v.treatmentName}</span>
                        <span>{v.name}</span>
                    </div>
                    <span className='font-mono text-sm text-muted-foreground ml-4 shrink-0'>
                        {formatCurrency(v.priceCents / 100)}
                    </span>
                </div>
            ),
        }));

        return [...bookableItemOptions, ...standaloneOptions];
    }, [serviceItems, standaloneVariantItems, variantsWithBundlesAvailable, isInfluencer, influencerPriceOverrides]);

    // Build autocomplete options for bundles
    const bundleItemOptions = useMemo(() => {
        return bundleItems.map((item) => {
            const priceDisplay = item.bundle
                ? formatCurrency(item.bundle.unitPriceGross * item.bundle.unitQuantity)
                : '';
            const bundleInfo = item.bundle
                ? `${item.bundle.unitQuantity}x Behandlungen`
                : '';

            return {
                label: item.nameDe,
                value: item.id,
                renderItem: (
                    <div className='w-full flex justify-between items-center'>
                        <div className='flex flex-col'>
                            <span className='text-xs text-muted-foreground'>
                                {bundleInfo}
                            </span>
                            <span>{item.nameDe}</span>
                        </div>
                        {priceDisplay && (
                            <span className='font-mono text-sm text-muted-foreground ml-4 shrink-0'>
                                {priceDisplay}
                            </span>
                        )}
                    </div>
                ),
            };
        });
    }, [bundleItems]);

    // Fetch products for adding product line items
    const { data: productsData, isLoading: productsLoading } = trpc.physicalProducts.searchProducts.useQuery({
        search: productSearchValue,
        limit: 50,
    });

    // Fetch gift card templates for adding gift card line items
    const { data: giftCardTemplatesData, isLoading: giftCardTemplatesLoading } = trpc.giftCardTemplates.searchTemplates.useQuery({
        search: giftCardSearchValue,
        limit: 50,
    });

    // Build autocomplete options for products
    const productItemOptions = useMemo(() => {
        if (!productsData?.data) { return []; }
        return productsData.data.map((product) => ({
            label: product.name,
            value: product.id,
            renderItem: (
                <div className='w-full flex justify-between items-center'>
                    <div className='flex flex-col'>
                        {product.description && (
                            <span className='text-xs text-muted-foreground truncate max-w-xs'>
                                {product.description}
                            </span>
                        )}
                        <span>{product.name}</span>
                    </div>
                    <span className='font-mono text-sm text-muted-foreground ml-4 shrink-0'>
                        {formatCurrency(Number(product.unitPriceGross))}
                    </span>
                </div>
            ),
        }));
    }, [productsData]);

    // Build autocomplete options for gift cards
    const giftCardItemOptions = useMemo(() => {
        if (!giftCardTemplatesData?.data) { return []; }
        return giftCardTemplatesData.data.map((template) => ({
            label: template.name,
            value: template.id,
            renderItem: (
                <div className='flex flex-col'>
                    <span className='text-xs text-muted-foreground'>
                        {template.type === 'USE_EVERYWHERE' ? 'Überall einsetzbar' : `${template.validTreatmentVariants.length} Variante(n)`}
                    </span>
                    <span>{template.name}</span>
                </div>
            ),
        }));
    }, [giftCardTemplatesData]);

    // Add a service as line item(s), auto-applying bundle voucher if available
    const addServiceAsLineItem = useCallback((
        item: BookableItem,
        variantId: string,
        addOns: SelectedAddOn[]
    ) => {
        const vatRate = 0.19;
        const variant = item.variantOptions.find(v => v.treatmentVariant?.id === variantId)?.treatmentVariant;
        if (!variant) { return; }

        // Check if customer has an available bundle voucher for this treatment variant
        const matchingVouchers = vouchersByVariant.get(variantId) || [];
        const bestVoucher = matchingVouchers.length > 0 ? matchingVouchers[0] : null;

        const originalGross = variant.priceCents / 100;

        // For influencer drafts, use bundle price override if available
        const influencerOverride = isInfluencer ? influencerPriceOverrides?.[variantId] : undefined;
        const hasInfluencerOverride = influencerOverride !== undefined;

        // Priority: influencer override > voucher price > original price
        const totalGross = hasInfluencerOverride
            ? influencerOverride
            : bestVoucher ? bestVoucher.unitPriceGross : originalGross;
        const totalNet = totalGross / (1 + vatRate);
        const vatAmount = totalGross - totalNet;

        const newItem: BillingDraftLineItem = {
            description: variant.name,
            sequence: fields.length + 1,
            servicePillarType: 'AESTHETIC',
            serviceMedicalType: 'NOT_MEDICAL',
            sourceType: 'UPSELL',
            totalGross,
            totalGrossDiscounted: totalGross,
            grossDiscountAmount: 0,
            vatRate,
            totalNet,
            totalNetDiscounted: totalNet,
            netDiscountAmount: 0,
            vatAmount,
            vatAmountDiscounted: vatAmount,
            treatmentVariantId: variant.id,
            physicalProductId: null,
            bookableItemId: item.id,
            bookableItemAddonId: null,
            cartItemSourceId: null,
            bundleId: null,
            consumeFirstCharge: undefined,
            paidByBundleVoucherId: hasInfluencerOverride ? null : (bestVoucher?.id ?? null),
            originalPriceGross: (hasInfluencerOverride || bestVoucher) ? originalGross : undefined,
            discounts: [],
        };

        append(newItem);

        // Also add any selected add-ons as separate line items
        for (const addOn of addOns) {
            // Check voucher for add-on treatment variant too
            const addOnVouchers = vouchersByVariant.get(addOn.variantId) || [];
            const addOnVoucher = addOnVouchers.length > 0 ? addOnVouchers[0] : null;

            const addOnOriginalGross = addOn.priceCents / 100;

            const addOnInfluencerOverride = isInfluencer ? influencerPriceOverrides?.[addOn.variantId] : undefined;
            const hasAddOnInfluencerOverride = addOnInfluencerOverride !== undefined;

            const addOnGross = hasAddOnInfluencerOverride
                ? addOnInfluencerOverride
                : addOnVoucher ? addOnVoucher.unitPriceGross : addOnOriginalGross;
            const addOnNet = addOnGross / (1 + vatRate);
            const addOnVat = addOnGross - addOnNet;

            const addOnItem: BillingDraftLineItem = {
                description: addOn.addOnName,
                sequence: fields.length + 2,
                servicePillarType: 'AESTHETIC',
                serviceMedicalType: 'NOT_MEDICAL',
                sourceType: 'UPSELL',
                totalGross: addOnGross,
                totalGrossDiscounted: addOnGross,
                grossDiscountAmount: 0,
                vatRate,
                totalNet: addOnNet,
                totalNetDiscounted: addOnNet,
                netDiscountAmount: 0,
                vatAmount: addOnVat,
                vatAmountDiscounted: addOnVat,
                treatmentVariantId: addOn.variantId,
                physicalProductId: null,
                bookableItemId: item.id,
                bookableItemAddonId: addOn.addOnId,
                cartItemSourceId: null,
                bundleId: null,
                consumeFirstCharge: undefined,
                paidByBundleVoucherId: hasAddOnInfluencerOverride ? null : (addOnVoucher?.id ?? null),
                originalPriceGross: (hasAddOnInfluencerOverride || addOnVoucher) ? addOnOriginalGross : undefined,
                discounts: [],
            };

            append(addOnItem);
        }

        // Recalculate discounts including the new item(s)
        recalculateAllDiscounts(discountDefsRef.current);

        // Reset state
        setServiceSearchValue('');
        setServiceSelectedValue('');
        setVariantDialogOpen(false);
        setSelectedBookableItem(null);
        setSelectedVariantId(null);
        setSelectedAddOns([]);
    }, [fields.length, append, vouchersByVariant, recalculateAllDiscounts, isInfluencer, influencerPriceOverrides]);

    // Add a bundle as line item
    const addBundleAsLineItem = useCallback((item: BookableItem) => {
        if (!item.bundle) { return; }
        
        const vatRate = 0.19;
        const totalGross = item.bundle.unitPriceGross * item.bundle.unitQuantity;
        const totalNet = totalGross / (1 + vatRate);
        const vatAmount = totalGross - totalNet;

        const variant = item.bundle.treatmentVariant;

        const newItem: BillingDraftLineItem = {
            description: item.nameDe,
            sequence: fields.length + 1,
            servicePillarType: 'AESTHETIC',
            serviceMedicalType: 'NOT_MEDICAL',
            sourceType: 'UPSELL',
            totalGross,
            totalGrossDiscounted: totalGross,
            grossDiscountAmount: 0,
            vatRate,
            totalNet,
            totalNetDiscounted: totalNet,
            netDiscountAmount: 0,
            vatAmount,
            vatAmountDiscounted: vatAmount,
            treatmentVariantId: variant?.id ?? null,
            physicalProductId: null,
            bookableItemId: item.id,
            bookableItemAddonId: null,
            cartItemSourceId: null,
            bundleId: item.bundle.id,
            consumeFirstCharge: true, // Default to true for bundles
            paidByBundleVoucherId: null,
            originalPriceGross: undefined,
            discounts: [],
        };

        append(newItem);

        // Recalculate discounts including the new item
        recalculateAllDiscounts(discountDefsRef.current);

        // Reset state
        setBundleSearchValue('');
        setBundleSelectedValue('');
    }, [fields.length, append, recalculateAllDiscounts]);

    // Add a standalone treatment variant (not linked to any bookable item) as a line item
    const addStandaloneVariantAsLineItem = useCallback((variant: {
        id: string;
        name: string;
        priceCents: number;
        treatmentName: string;
        servicePillarType: string;
        serviceMedicalType: string;
    }) => {
        const vatRate = 0.19;
        const originalGross = variant.priceCents / 100;

        const matchingVouchers = vouchersByVariant.get(variant.id) || [];
        const bestVoucher = matchingVouchers.length > 0 ? matchingVouchers[0] : null;
        const influencerOverride = isInfluencer ? influencerPriceOverrides?.[variant.id] : undefined;
        const hasInfluencerOverride = influencerOverride !== undefined;

        const totalGross = hasInfluencerOverride
            ? influencerOverride
            : bestVoucher ? bestVoucher.unitPriceGross : originalGross;
        const totalNet = totalGross / (1 + vatRate);
        const vatAmount = totalGross - totalNet;

        const newItem: BillingDraftLineItem = {
            description: variant.name,
            sequence: fields.length + 1,
            servicePillarType: variant.servicePillarType as 'AESTHETIC' | 'COSMETIC' | 'HEALTH',
            serviceMedicalType: variant.serviceMedicalType as 'MEDICAL' | 'NOT_MEDICAL',
            sourceType: 'UPSELL',
            totalGross,
            totalGrossDiscounted: totalGross,
            grossDiscountAmount: 0,
            vatRate,
            totalNet,
            totalNetDiscounted: totalNet,
            netDiscountAmount: 0,
            vatAmount,
            vatAmountDiscounted: vatAmount,
            treatmentVariantId: variant.id,
            physicalProductId: null,
            bookableItemId: null,
            bookableItemAddonId: null,
            cartItemSourceId: null,
            bundleId: null,
            consumeFirstCharge: undefined,
            paidByBundleVoucherId: hasInfluencerOverride ? null : (bestVoucher?.id ?? null),
            originalPriceGross: (hasInfluencerOverride || bestVoucher) ? originalGross : undefined,
            discounts: [],
        };

        append(newItem);
        recalculateAllDiscounts(discountDefsRef.current);
    }, [fields.length, append, vouchersByVariant, recalculateAllDiscounts, isInfluencer, influencerPriceOverrides]);

    // Handle service selection from autocomplete
    const handleServiceSelect = useCallback((value: string) => {
        if (!value) { return; }

        if (value.startsWith('sv_')) {
            const variantId = value.slice(3);
            const variant = standaloneVariantItems.find(v => v.id === variantId);
            if (!variant) { return; }
            addStandaloneVariantAsLineItem(variant);
        } else if (value.startsWith('bi_')) {
            const itemId = value.slice(3);
            const item = serviceItems.find((i) => i.id === itemId);
            if (!item) { return; }

            setSelectedBookableItem(item);
            setSelectedVariantId(null);
            setSelectedAddOns([]);

            if (item.variantOptions.length === 1 && item.addOns.length === 0) {
                const variant = item.variantOptions[0]?.treatmentVariant;
                if (variant) {
                    addServiceAsLineItem(item, variant.id, []);
                }
            } else {
                setVariantDialogOpen(true);
                if (item.variantOptions.length > 0 && item.variantOptions[0]?.treatmentVariant) {
                    setSelectedVariantId(item.variantOptions[0].treatmentVariant.id);
                }
            }
        }

        setServiceSearchValue('');
        setServiceSelectedValue('');
    }, [serviceItems, standaloneVariantItems, addServiceAsLineItem, addStandaloneVariantAsLineItem]);

    // Handle bundle selection from autocomplete
    const handleBundleSelect = useCallback((value: string) => {
        if (!value) { return; }

        const item = bundleItems.find((i) => i.id === value);
        if (!item || !item.bundle) { return; }

        addBundleAsLineItem(item);
    }, [bundleItems, addBundleAsLineItem]);

    // Handle add-on toggle in variant dialog
    const handleAddOnToggle = useCallback((addOn: BookableItem['addOns'][0], checked: boolean) => {
        if (!addOn.treatmentVariant) { return; }

        if (checked) {
            setSelectedAddOns(prev => [...prev, {
                addOnId: addOn.id,
                addOnName: addOn.nameDe ?? addOn.treatmentVariant?.name ?? 'Add-On',
                variantId: addOn.treatmentVariant!.id,
                variantName: addOn.treatmentVariant!.name,
                priceCents: addOn.treatmentVariant!.priceCents,
            }]);
        } else {
            setSelectedAddOns(prev => prev.filter(a => a.addOnId !== addOn.id));
        }
    }, []);

    // Confirm variant/add-on selection for services
    const handleConfirmServiceSelection = useCallback(() => {
        if (!selectedBookableItem || !selectedVariantId) { return; }
        addServiceAsLineItem(selectedBookableItem, selectedVariantId, selectedAddOns);
    }, [selectedBookableItem, selectedVariantId, selectedAddOns, addServiceAsLineItem]);

    // Handle adding a physical product as a line item
    const handleAddProductLineItem = (product: any) => {
        const priceGross = Number(product.unitPriceGross);
        const vatRate = 0.19;
        const totalNet = priceGross / (1 + vatRate);
        const vatAmount = priceGross - totalNet;

        const newItem: BillingDraftLineItem = {
            description: product.name,
            sequence: fields.length + 1,
            servicePillarType: 'HEALTH',
            serviceMedicalType: 'NOT_MEDICAL',
            sourceType: 'PHYSICAL_PRODUCT',
            totalGross: priceGross,
            totalGrossDiscounted: priceGross,
            grossDiscountAmount: 0,
            vatRate,
            totalNet,
            totalNetDiscounted: totalNet,
            netDiscountAmount: 0,
            vatAmount,
            vatAmountDiscounted: vatAmount,
            treatmentVariantId: null,
            physicalProductId: product.id,
            bookableItemId: null,
            bookableItemAddonId: null,
            cartItemSourceId: null,
            bundleId: null,
            consumeFirstCharge: undefined,
            paidByBundleVoucherId: null,
            originalPriceGross: undefined,
            discounts: [],
        };

        append(newItem);
        // Recalculate discounts including the new item
        recalculateAllDiscounts(discountDefsRef.current);
        setProductSearchValue('');
        setProductSelectedValue('');
    };

    // Handle updating the gift card recipient customer
    const handleGiftCardRecipientChange = (index: number, customerId: number | null) => {
        const currentItem = form.getValues(`lineItems.${index}`) as BillingDraftLineItem;
        update(index, { ...currentItem, giftCardRecipientCustomerId: customerId });
    };

    // Handle adding a gift card template as a line item with a custom amount
    const handleAddGiftCardLineItem = (template: any, customAmount: number) => {
        const totalGross = customAmount;
        const vatRate = 0.19;
        const totalNet = totalGross / (1 + vatRate);
        const vatAmount = totalGross - totalNet;

        const newItem: BillingDraftLineItem = {
            description: template.name,
            sequence: fields.length + 1,
            servicePillarType: 'AESTHETIC',
            serviceMedicalType: 'NOT_MEDICAL',
            sourceType: 'UPSELL',
            totalGross,
            totalGrossDiscounted: totalGross,
            grossDiscountAmount: 0,
            vatRate,
            totalNet,
            totalNetDiscounted: totalNet,
            netDiscountAmount: 0,
            vatAmount,
            vatAmountDiscounted: vatAmount,
            treatmentVariantId: null,
            physicalProductId: null,
            bookableItemId: null,
            bookableItemAddonId: null,
            cartItemSourceId: null,
            bundleId: null,
            consumeFirstCharge: undefined,
            paidByBundleVoucherId: null,
            originalPriceGross: undefined,
            giftCardTemplateId: template.id,
            giftCardRecipientCustomerId: null,
            discounts: [],
        };

        append(newItem);
        recalculateAllDiscounts(discountDefsRef.current);
    };

    // Handle product selection from autocomplete
    const handleProductSelect = useCallback((value: string) => {
        if (!value) { return; }
        const product = productsData?.data.find((p) => p.id === value);
        if (!product) { return; }
        handleAddProductLineItem(product);
    }, [productsData, handleAddProductLineItem]);

    // Handle gift card selection from autocomplete — opens amount dialog
    const handleGiftCardSelect = useCallback((value: string) => {
        if (!value) { return; }
        const template = giftCardTemplatesData?.data.find((t) => t.id === value);
        if (!template) { return; }
        setPendingGiftCardTemplate(template);
        setGiftCardCustomAmount(Number(template.valueAmount).toFixed(2));
        setGiftCardAmountDialogOpen(true);
        setGiftCardSearchValue('');
        setGiftCardSelectedValue('');
    }, [giftCardTemplatesData]);

    // Confirm gift card amount and add line item
    const handleConfirmGiftCardAmount = useCallback(() => {
        if (!pendingGiftCardTemplate) { return; }
        const amount = parseFloat(giftCardCustomAmount);
        if (isNaN(amount) || amount <= 0) { return; }
        handleAddGiftCardLineItem(pendingGiftCardTemplate, amount);
        setGiftCardAmountDialogOpen(false);
        setPendingGiftCardTemplate(null);
        setGiftCardCustomAmount('');
    }, [pendingGiftCardTemplate, giftCardCustomAmount, handleAddGiftCardLineItem]);

    // Handle voucher selection change for a line item
    const handleVoucherChange = (index: number, voucherId: string | null) => {
        const currentItem = form.getValues(`lineItems.${index}`) as BillingDraftLineItem;
        const vatRate = currentItem.vatRate;

        // Compute the bare price (no discounts, no voucher)
        const barePrice = currentItem.originalPriceGross ?? currentItem.totalGross;

        if (!voucherId || voucherId === 'none') {
            // Remove voucher - restore to bare price, clear discounts for recalculation
            const totalGross = barePrice;
            const totalNet = totalGross / (1 + vatRate);
            const vatAmount = totalGross - totalNet;

            update(index, {
                ...currentItem,
                paidByBundleVoucherId: null,
                totalGross,
                totalGrossDiscounted: totalGross,
                grossDiscountAmount: 0,
                totalNet,
                totalNetDiscounted: totalNet,
                netDiscountAmount: 0,
                vatAmount,
                vatAmountDiscounted: vatAmount,
                originalPriceGross: undefined,
                discounts: [],
            });
        } else {
            // Apply voucher
            const voucher = availableVouchers?.find(v => v.id === voucherId);
            if (!voucher) { return; }

            const totalGross = voucher.unitPriceGross;
            const totalNet = totalGross / (1 + vatRate);
            const vatAmount = totalGross - totalNet;

            update(index, {
                ...currentItem,
                paidByBundleVoucherId: voucherId,
                totalGross,
                totalGrossDiscounted: totalGross,
                grossDiscountAmount: 0,
                totalNet,
                totalNetDiscounted: totalNet,
                netDiscountAmount: 0,
                vatAmount,
                vatAmountDiscounted: vatAmount,
                originalPriceGross: barePrice,
                discounts: [],
            });
        }

        // Recalculate discounts based on the new base price
        recalculateAllDiscounts(discountDefsRef.current);
    };

    const handleUpdateLineItem = (index: number, updates: Partial<BillingDraftLineItem>) => {
        const currentItem = form.getValues(`lineItems.${index}`) as BillingDraftLineItem;
        const updatedItem = { ...currentItem, ...updates };
        const priceChanged = updates.totalGross !== undefined;

        if (priceChanged) {
            const basePrice = updates.totalGross!;
            const vatRate = currentItem.vatRate;
            const baseNet = basePrice / (1 + vatRate);
            const baseVat = basePrice - baseNet;
            updatedItem.totalGross = basePrice;
            updatedItem.totalNet = baseNet;
            updatedItem.vatAmount = baseVat;
            updatedItem.totalGrossDiscounted = basePrice;
            updatedItem.grossDiscountAmount = 0;
            updatedItem.totalNetDiscounted = baseNet;
            updatedItem.netDiscountAmount = 0;
            updatedItem.vatAmountDiscounted = baseVat;
            updatedItem.discounts = [];
        }

        update(index, updatedItem);

        if (priceChanged) {
            recalculateAllDiscounts(discountDefsRef.current);
        }
    };

    const handleRemoveLineItem = (index: number) => {
        remove(index);
        
        // Re-sequence remaining items
        const updatedFields = form.getValues('lineItems');
        updatedFields.forEach((item, i) => {
            form.setValue(`lineItems.${i}.sequence`, i + 1);
        });

        // Recalculate discounts (redistributes absolute discounts among remaining items)
        recalculateAllDiscounts(discountDefsRef.current);
    };

    const isStoreSelected = selectedStoreId > 0;

    return (
        <div className='space-y-6'>
            {/* Line Items Table */}
            <Card>
                <CardHeader className='pb-3 space-y-4'>
                    <CardTitle className='text-base'>Positionen hinzufügen</CardTitle>
                    
                    {!isStoreSelected && (
                        <div className='p-4 bg-muted rounded-lg text-sm text-muted-foreground'>
                            Bitte wählen Sie zuerst eine Filiale aus, um Leistungen, Bundles oder Produkte hinzuzufügen.
                        </div>
                    )}

                    {isStoreSelected && (
                        <div className='grid grid-cols-2 gap-4'>
                            <div className={cn('space-y-1.5', isServicesDisabled && 'opacity-50 pointer-events-none')}>
                                <Label className='text-sm text-muted-foreground flex items-center gap-1.5'>
                                    <Sparkles className='h-3.5 w-3.5' />
                                    Leistungen
                                    {isServicesDisabled && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <AlertTriangle className='h-3.5 w-3.5 text-amber-500' />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Leistungen können nur hinzugefügt werden, wenn ein Termin verknüpft ist.</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                </Label>
                                <AutoComplete
                                    selectedValue={serviceSelectedValue}
                                    onSelectedValueChange={(value) => {
                                        setServiceSelectedValue(value);
                                        handleServiceSelect(value);
                                    }}
                                    searchValue={serviceSearchValue}
                                    onSearchValueChange={setServiceSearchValue}
                                    items={serviceItemOptions}
                                    placeholder='Leistung suchen...'
                                    emptyMessage='Keine Leistung gefunden'
                                    isLoading={serviceSearchQuery.isLoading}
                                    resetSearchOnSelectItem
                                />
                                {isServicesDisabled && (
                                    <p className='text-xs text-amber-600'>
                                        Bitte verknüpfe zuerst einen Termin, um Leistungen hinzuzufügen.
                                    </p>
                                )}
                            </div>

                            <div className={cn('space-y-1.5', isProductsDisabled && 'opacity-50 pointer-events-none')}>
                                <Label className='text-sm text-muted-foreground flex items-center gap-1.5'>
                                    <ShoppingBag className='h-3.5 w-3.5' />
                                    Produkte
                                </Label>
                                <AutoComplete
                                    selectedValue={productSelectedValue}
                                    onSelectedValueChange={(value) => {
                                        setProductSelectedValue(value);
                                        handleProductSelect(value);
                                    }}
                                    searchValue={productSearchValue}
                                    onSearchValueChange={setProductSearchValue}
                                    items={productItemOptions}
                                    placeholder='Produkt suchen...'
                                    emptyMessage='Keine Produkte gefunden'
                                    isLoading={productsLoading}
                                    resetSearchOnSelectItem
                                />
                            </div>

                            <div className={cn('space-y-1.5', isBundlesAndGiftCardsDisabled && 'opacity-50 pointer-events-none')}>
                                <Label className='text-sm text-muted-foreground flex items-center gap-1.5'>
                                    <Gift className='h-3.5 w-3.5' />
                                    Bundles
                                </Label>
                                <AutoComplete
                                    selectedValue={bundleSelectedValue}
                                    onSelectedValueChange={(value) => {
                                        setBundleSelectedValue(value);
                                        handleBundleSelect(value);
                                    }}
                                    searchValue={bundleSearchValue}
                                    onSearchValueChange={setBundleSearchValue}
                                    items={bundleItemOptions}
                                    placeholder='Bundle suchen...'
                                    emptyMessage='Kein Bundle gefunden'
                                    isLoading={bundleBookableItemsQuery.isLoading}
                                    resetSearchOnSelectItem
                                />
                            </div>

                            <div className={cn('space-y-1.5', isBundlesAndGiftCardsDisabled && 'opacity-50 pointer-events-none')}>
                                <Label className='text-sm text-muted-foreground flex items-center gap-1.5'>
                                    <CreditCard className='h-3.5 w-3.5' />
                                    Wertgutscheine
                                </Label>
                                <AutoComplete
                                    selectedValue={giftCardSelectedValue}
                                    onSelectedValueChange={(value) => {
                                        setGiftCardSelectedValue(value);
                                        handleGiftCardSelect(value);
                                    }}
                                    searchValue={giftCardSearchValue}
                                    onSearchValueChange={setGiftCardSearchValue}
                                    items={giftCardItemOptions}
                                    placeholder='Wertgutschein suchen...'
                                    emptyMessage='Keine Wertgutschein-Vorlagen gefunden'
                                    isLoading={giftCardTemplatesLoading}
                                    resetSearchOnSelectItem
                                />
                            </div>
                        </div>
                    )}
                </CardHeader>

                {/* Service Variant/Add-on Selection Dialog */}
                <Dialog open={variantDialogOpen} onOpenChange={setVariantDialogOpen}>
                    <DialogContent className='max-w-md'>
                        <DialogHeader>
                            <DialogTitle>
                                {selectedBookableItem?.nameDe} konfigurieren
                            </DialogTitle>
                        </DialogHeader>

                        <div className='space-y-4'>
                            {/* Variant Selection */}
                            {selectedBookableItem && selectedBookableItem.variantOptions.length > 1 && (
                                <div className='space-y-2'>
                                    <Label>Variante auswählen</Label>
                                    <div className='grid gap-2'>
                                        {selectedBookableItem.variantOptions.map((opt) => {
                                            if (!opt.treatmentVariant) { return null; }
                                            const hasBundleAvailable = variantsWithBundlesAvailable.has(opt.treatmentVariant.id);
                                            const variantOverridePrice = isInfluencer ? influencerPriceOverrides?.[opt.treatmentVariant.id] : undefined;
                                            const displayPrice = variantOverridePrice !== undefined
                                                ? variantOverridePrice
                                                : opt.treatmentVariant.priceCents / 100;

                                            return (
                                                <button
                                                    key={opt.treatmentVariant.id}
                                                    type='button'
                                                    className={`p-3 rounded-lg border text-left transition-colors ${
                                                        selectedVariantId === opt.treatmentVariant.id
                                                            ? 'border-primary bg-primary/5'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    onClick={() => setSelectedVariantId(opt.treatmentVariant!.id)}
                                                >
                                                    <div className='flex items-center gap-2'>
                                                        <span className='font-medium'>{opt.treatmentVariant.name}</span>
                                                        {hasBundleAvailable && (
                                                            <Badge variant='default' className='text-[10px] px-1.5 py-0 bg-blue-600'>
                                                                <Package className='h-3 w-3 mr-0.5' />
                                                                Bundle
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className='text-sm text-muted-foreground'>
                                                        {variantOverridePrice !== undefined && (
                                                            <span className='line-through mr-1.5'>{formatCurrency(opt.treatmentVariant.priceCents / 100)}</span>
                                                        )}
                                                        {formatCurrency(displayPrice)}
                                                        {variantOverridePrice !== undefined && (
                                                            <span className='text-orange-600 ml-1 text-xs'>(Bundle-Preis)</span>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Add-ons Selection */}
                            {selectedBookableItem && selectedBookableItem.addOns.length > 0 && (
                                <div className='space-y-2'>
                                    <Label>Add-Ons (optional)</Label>
                                    <div className='grid gap-2'>
                                        {selectedBookableItem.addOns.map((addOn) => {
                                            if (!addOn.treatmentVariant) { return null; }
                                            const hasBundleAvailable = variantsWithBundlesAvailable.has(addOn.treatmentVariant.id);
                                            const addOnOverridePrice = isInfluencer ? influencerPriceOverrides?.[addOn.treatmentVariant.id] : undefined;

                                            return (
                                                <label
                                                    key={addOn.id}
                                                    className='flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer'
                                                >
                                                    <Checkbox
                                                        checked={selectedAddOns.some(a => a.addOnId === addOn.id)}
                                                        onCheckedChange={(checked) => handleAddOnToggle(addOn, !!checked)}
                                                    />
                                                    <div className='flex-1'>
                                                        <div className='flex items-center gap-2'>
                                                            <span className='font-medium'>{addOn.nameDe || addOn.treatmentVariant.name}</span>
                                                            {hasBundleAvailable && (
                                                                <Badge variant='default' className='text-[10px] px-1.5 py-0 bg-blue-600'>
                                                                    <Package className='h-3 w-3 mr-0.5' />
                                                                    Bundle
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className='text-sm text-muted-foreground'>
                                                            {addOnOverridePrice !== undefined ? (
                                                                <>
                                                                    +<span className='line-through mr-1.5'>{formatCurrency(addOn.treatmentVariant.priceCents / 100)}</span>
                                                                    {formatCurrency(addOnOverridePrice)}
                                                                    <span className='text-orange-600 ml-1 text-xs'>(Bundle-Preis)</span>
                                                                </>
                                                            ) : (
                                                                <>+{formatCurrency(addOn.treatmentVariant.priceCents / 100)}</>
                                                            )}
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant='outline' onClick={() => setVariantDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handleConfirmServiceSelection}
                                disabled={!selectedVariantId}
                            >
                                Hinzufügen
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Gift Card Amount Dialog */}
                <Dialog open={giftCardAmountDialogOpen} onOpenChange={(open) => {
                    setGiftCardAmountDialogOpen(open);
                    if (!open) {
                        setPendingGiftCardTemplate(null);
                        setGiftCardCustomAmount('');
                    }
                }}>
                    <DialogContent className='max-w-sm'>
                        <DialogHeader>
                            <DialogTitle>Wertgutschein-Betrag</DialogTitle>
                            <DialogDescription>
                                Legen Sie den Betrag für den Wertgutschein fest.
                            </DialogDescription>
                        </DialogHeader>

                        <div className='space-y-4'>
                            {pendingGiftCardTemplate && (
                                <div className='text-sm text-muted-foreground'>
                                    {pendingGiftCardTemplate.name}
                                </div>
                            )}
                            <div className='space-y-2'>
                                <Label htmlFor='gift-card-amount'>Betrag (EUR)</Label>
                                <Input
                                    id='gift-card-amount'
                                    type='number'
                                    step='0.01'
                                    min='0.01'
                                    value={giftCardCustomAmount}
                                    onChange={(e) => setGiftCardCustomAmount(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleConfirmGiftCardAmount();
                                        }
                                    }}
                                    className='font-mono'
                                    autoFocus
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant='outline' onClick={() => {
                                setGiftCardAmountDialogOpen(false);
                                setPendingGiftCardTemplate(null);
                                setGiftCardCustomAmount('');
                            }}>
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handleConfirmGiftCardAmount}
                                disabled={!giftCardCustomAmount || isNaN(parseFloat(giftCardCustomAmount)) || parseFloat(giftCardCustomAmount) <= 0}
                            >
                                Hinzufügen
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <CardContent className='p-0'>
                    {fields.length === 0 && noTreatmentPerformed ? (
                        <div className='p-8 text-center space-y-2'>
                            <Badge className='bg-red-100 text-red-800 border-red-300 text-sm px-3 py-1' variant='outline'>
                                Keine Behandlung durchgeführt
                            </Badge>
                            {noTreatmentPractitioners && noTreatmentPractitioners.length > 0 && (
                                <div className='flex justify-center gap-2 flex-wrap mt-2'>
                                    {noTreatmentPractitioners.map((name) => (
                                        <Badge key={name} className='text-[10px] px-1.5 py-0 bg-green-100 text-green-800 border-green-300' variant='outline'>
                                            {name}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : fields.length === 0 ? (
                        <div className='p-8 text-center text-muted-foreground'>
                            <p>Keine Positionen vorhanden.</p>
                            <p className='text-sm mt-2'>
                                Wählen Sie oben einen Tab und suchen Sie nach Leistungen, Bundles oder Produkten.
                            </p>
                        </div>
                    ) : (
                        <div className='bg-[#FF6439]/10 border-t border-[#FF6439]/30 rounded-b-lg'>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className='w-12'>Pos.</TableHead>
                                    <TableHead>Beschreibung</TableHead>
                                    <TableHead className='w-40'>Dokumentiert von</TableHead>
                                    <TableHead className='w-28 text-right'>Brutto</TableHead>
                                    <TableHead className='w-48 text-center'>Bundle</TableHead>
                                    <TableHead className='w-16'></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {fields.map((field, index) => {
                                    const item = currentLineItems[index];
                                    if (!item) { return null; }
                                    const isEditing = editingIndex === index;
                                    const isBundle = !!item.bundleId;
                                    const isGiftCard = !!item.giftCardTemplateId;
                                    const hasPaidByVoucher = !!item.paidByBundleVoucherId;
                                    const hasInfluencerBundleOverride = isInfluencer
                                        && !!item.treatmentVariantId
                                        && !isBundle
                                        && !!influencerPriceOverrides?.[item.treatmentVariantId];

                                    // Get available vouchers for this line item's treatment variant
                                    const variantId = item.treatmentVariantId;
                                    const lineItemVouchers = variantId
                                        ? (availableVouchers ?? []).filter(v => v.treatmentVariantId === variantId)
                                        : [];
                                    const hasVoucherOptions = lineItemVouchers.length > 0 && !isBundle;

                                    return (
                                        <TableRow key={field.id}>
                                            <TableCell className='font-mono text-sm border-l-[5px] border-l-green-500'>
                                                {index + 1}
                                            </TableCell>
                                            <TableCell>
                                                <div className='space-y-1'>
                                                    <div className='flex items-center gap-2'>
                                                        {isBundle && (
                                                            <Badge variant='secondary' className='gap-1'>
                                                                <Gift className='h-3 w-3' />
                                                                Bundle
                                                            </Badge>
                                                        )}
                                                        {isGiftCard && (
                                                            <Badge variant='secondary' className='gap-1 bg-purple-100 text-purple-800'>
                                                                <CreditCard className='h-3 w-3' />
                                                                Wertgutschein
                                                            </Badge>
                                                        )}
                                                        {hasPaidByVoucher && !isBundle && (
                                                            <Badge variant='default' className='gap-1 bg-green-600'>
                                                                <Ticket className='h-3 w-3' />
                                                                Bundle
                                                            </Badge>
                                                        )}
                                                        {hasInfluencerBundleOverride && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Badge variant='outline' className='gap-1 text-[10px] px-1.5 py-0 border-orange-400 text-orange-700 bg-orange-50'>
                                                                        <Package className='h-3 w-3' />
                                                                        Bundle-Preis
                                                                    </Badge>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p className='max-w-xs text-sm'>
                                                                        Preis wurde auf den Bundle-Einzelpreis angepasst
                                                                        {item.originalPriceGross !== undefined && (
                                                                            <> (statt {formatCurrency(item.originalPriceGross)})</>
                                                                        )}
                                                                    </p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {isEditing ? (
                                                            <Input
                                                                value={item.description}
                                                                onChange={(e) => handleUpdateLineItem(index, { description: e.target.value })}
                                                                className='h-8'
                                                            />
                                                        ) : (
                                                            <div
                                                                className='cursor-pointer hover:text-primary'
                                                                onClick={() => setEditingIndex(index)}
                                                            >
                                                                {item.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {item.discounts && item.discounts.length > 0 && (
                                                        <div className='flex flex-wrap gap-1'>
                                                            {item.discounts.map((d, di) => (
                                                                <Badge key={di} variant='outline' className='text-[10px] px-1.5 py-0 gap-0.5 text-green-700 border-green-300'>
                                                                    <Tag className='h-2.5 w-2.5' />
                                                                    {d.label}: -{formatCurrency(d.discountAmountEur)}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {isGiftCard && (
                                                        <GiftCardRecipientPicker
                                                            index={index}
                                                            currentRecipientId={item.giftCardRecipientCustomerId}
                                                            onRecipientChange={handleGiftCardRecipientChange}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className='w-40'>
                                                {item.documentedByPractitioner ? (
                                                    <Badge className='text-[10px] px-1.5 py-0 bg-green-100 text-green-800 border-green-300' variant='outline'>
                                                        {item.documentedByPractitioner}
                                                    </Badge>
                                                ) : !isGiftCard && (item.treatmentVariantId || item.bundleId || item.physicalProductId) ? (
                                                    <Badge className='text-[10px] px-1.5 py-0 bg-orange-100 text-orange-800 border-orange-300' variant='outline'>
                                                        nicht dokumentiert
                                                    </Badge>
                                                ) : null}
                                            </TableCell>
                                            <TableCell className='text-right'>
                                                {item.grossDiscountAmount > 0 && (
                                                    <div className='text-xs text-muted-foreground line-through font-mono mb-0.5'>
                                                        {formatCurrency(item.totalGross)}
                                                    </div>
                                                )}
                                                <Input
                                                    type='text'
                                                    inputMode='decimal'
                                                    defaultValue={item.totalGross.toFixed(2).replace('.', ',')}
                                                    key={`price-${index}-${item.totalGross}`}
                                                    onBlur={(e) => {
                                                        const parsed = parseFloat(e.target.value.replace(',', '.'));
                                                        if (!isNaN(parsed) && parsed >= 0) {
                                                            handleUpdateLineItem(index, { totalGross: parsed });
                                                        } else {
                                                            e.target.value = item.totalGross.toFixed(2).replace('.', ',');
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    className='h-8 w-24 text-right font-mono'
                                                />
                                                {item.grossDiscountAmount > 0 && (
                                                    <div className='text-xs text-green-600 font-mono mt-0.5'>
                                                        {formatCurrency(item.totalGrossDiscounted)}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className='text-center'>
                                                {isBundle ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div className='flex items-center justify-center gap-2'>
                                                                <Checkbox
                                                                    id={`consume-first-${field.id}`}
                                                                    checked={item.consumeFirstCharge ?? true}
                                                                    onCheckedChange={(checked) => 
                                                                        handleUpdateLineItem(index, { consumeFirstCharge: !!checked })
                                                                    }
                                                                />
                                                                <label
                                                                    htmlFor={`consume-first-${field.id}`}
                                                                    className='text-xs cursor-pointer'
                                                                >
                                                                    Erste Nutzung
                                                                </label>
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p className='max-w-xs text-sm'>
                                                                Wenn aktiviert, wird nach vollständiger Bezahlung des Auftrags 
                                                                automatisch eine Einheit des Bundle-Gutscheins für diese Behandlung eingelöst.
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                ) : hasVoucherOptions ? (
                                                    <Select
                                                        value={item.paidByBundleVoucherId ?? 'none'}
                                                        onValueChange={(value) => handleVoucherChange(index, value === 'none' ? null : value)}
                                                    >
                                                        <SelectTrigger className='h-8 w-full text-xs'>
                                                            <SelectValue placeholder='Kein Wertgutschein' />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value='none'>
                                                                <span className='text-muted-foreground'>Kein Bundle</span>
                                                            </SelectItem>
                                                            {lineItemVouchers.map((voucher) => (
                                                                <SelectItem key={voucher.id} value={voucher.id}>
                                                                    <div className='flex flex-col'>
                                                                        <span className='text-xs'>
                                                                            {formatCurrency(voucher.unitPriceGross)} ({voucher.remainingUnits} verbl.)
                                                                        </span>
                                                                        <span className='text-xs text-muted-foreground'>
                                                                            {voucher.sourceBillingDraftNumber}
                                                                        </span>
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <span className='text-muted-foreground text-xs'>—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    className='h-8 w-8 text-destructive'
                                                    onClick={() => handleRemoveLineItem(index)}
                                                >
                                                    <Trash2 className='h-4 w-4' />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Discounts & Totals Section */}
            {currentLineItems.length > 0 && (
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 items-start'>
                {!isDiscountsDisabled && <Card>
                    <CardHeader className='pb-3'>
                        <CardTitle className='text-base'>Rabatte</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {/* Applied discount definition badges */}
                        {discountDefinitions.length > 0 && (
                            <div className='flex flex-wrap gap-2'>
                                {discountDefinitions.map(def => {
                                    const isOverridden = def.discountCodeId ? overriddenDiscountCodeIds.has(def.discountCodeId) : false;
                                    return (
                                        <Badge key={def.id} variant='outline' className={`gap-1 pr-1 ${isOverridden ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-green-300 bg-green-50 text-green-800'}`}>
                                            {isOverridden && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <AlertTriangle className='h-3 w-3 text-amber-500' />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Kundentyp-Einschränkung wurde überschrieben</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                            {def.source === 'manual' ? <Percent className='h-3 w-3' /> : <Tag className='h-3 w-3' />}
                                            {def.label}
                                            {def.type === 'PERCENTAGE'
                                                ? ` (${def.value}%)`
                                                : ` (${formatCurrency(def.value)})`}
                                            {!def.targetAll && def.targetIndices.length > 0 && (
                                                <span className='text-muted-foreground text-[9px] ml-0.5'>
                                                    Pos. {def.targetIndices.map(i => i + 1).join(', ')}
                                                </span>
                                            )}
                                            <button
                                                type='button'
                                                onClick={() => handleRemoveDiscount(def.id)}
                                                className='ml-1 rounded-full hover:bg-muted p-0.5'
                                            >
                                                <X className='h-3 w-3' />
                                            </button>
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}

                        {/* Discount code selector + Manual discount form */}
                        <div className='space-y-4'>
                            {/* Discount code selector */}
                            {validDiscountCodes.length > 0 && (
                                <div className='space-y-1.5'>
                                    <Label className='text-sm font-medium'>Rabattcode anwenden</Label>
                                    <AutoComplete
                                        selectedValue={discountCodeSelectedValue}
                                        onSelectedValueChange={(value) => {
                                            setDiscountCodeSelectedValue(value);
                                            handleApplyDiscountCode(value);
                                            setDiscountCodeSelectedValue('');
                                        }}
                                        searchValue={discountCodeSearchValue}
                                        onSearchValueChange={setDiscountCodeSearchValue}
                                        items={discountCodeOptions}
                                        placeholder='Rabattcode suchen...'
                                        emptyMessage='Keine Rabattcodes verfügbar'
                                        resetSearchOnSelectItem
                                    />
                                    {appointmentCouponCodes && (
                                        <div className='px-3 py-2 rounded-md bg-yellow-100 text-red-600 text-sm font-medium'>
                                            Vom Kunden angegebener Rabattcode: {appointmentCouponCodes}
                                        </div>
                                    )}
                                </div>
                            )}

                            {validDiscountCodes.length === 0 && appointmentCouponCodes && (
                                <div className='space-y-1.5'>
                                    <div className='px-3 py-2 rounded-md bg-yellow-100 text-red-600 text-sm font-medium'>
                                        Rabattcode: {appointmentCouponCodes}
                                    </div>
                                </div>
                            )}

                            {/* Manual discount form */}
                            <div className='space-y-3'>
                                <Button
                                    type='button'
                                    variant='ghost'
                                    size='sm'
                                    className='text-sm font-medium px-0 hover:bg-transparent'
                                    onClick={() => setShowManualDiscountForm(v => !v)}
                                >
                                    {showManualDiscountForm ? <ChevronDown className='h-4 w-4 mr-1' /> : <ChevronRight className='h-4 w-4 mr-1' />}
                                    Manueller Rabatt
                                </Button>
                                {showManualDiscountForm && (
                                <>
                                <div className='grid grid-cols-2 gap-3'>
                                    <div className='space-y-1'>
                                        <Label className='text-xs text-muted-foreground'>Typ</Label>
                                        <Select
                                            value={manualDiscountType}
                                            onValueChange={(v) => setManualDiscountType(v as 'ABSOLUTE' | 'PERCENTAGE')}
                                        >
                                            <SelectTrigger className='h-8'>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='ABSOLUTE'>Absolut (EUR)</SelectItem>
                                                <SelectItem value='PERCENTAGE'>Prozentual (%)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className='space-y-1'>
                                        <Label className='text-xs text-muted-foreground'>Wert</Label>
                                        <Input
                                            type='number'
                                            step={manualDiscountType === 'PERCENTAGE' ? '1' : '0.01'}
                                            min='0'
                                            placeholder={manualDiscountType === 'PERCENTAGE' ? '10' : '5.00'}
                                            value={manualDiscountValue}
                                            onChange={(e) => setManualDiscountValue(e.target.value)}
                                            className='h-8'
                                        />
                                    </div>
                                </div>
                                <div className='grid grid-cols-[1fr_auto] gap-3 items-end'>
                                    <div className='space-y-1'>
                                        <Label className='text-xs text-muted-foreground'>Notiz (Pflichtfeld)</Label>
                                        <Textarea
                                            placeholder='Rabattgrund eingeben...'
                                            value={manualDiscountNote}
                                            onChange={(e) => setManualDiscountNote(e.target.value)}
                                            className='min-h-[2rem] resize-none'
                                            rows={2}
                                        />
                                    </div>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                        onClick={handleApplyManualDiscount}
                                        disabled={!manualDiscountValue || parseFloat(manualDiscountValue) <= 0 || !manualDiscountNote.trim() || currentLineItems.length === 0}
                                        className='h-8'
                                    >
                                        Anwenden
                                    </Button>
                                </div>
                                <div className='space-y-2'>
                                    <div className='flex items-center space-x-2'>
                                        <Checkbox
                                            id='manual-discount-all'
                                            checked={manualDiscountTargetAll}
                                            onCheckedChange={(checked) => {
                                                setManualDiscountTargetAll(!!checked);
                                                if (checked) { setManualDiscountTargetIndices([]); }
                                            }}
                                        />
                                        <Label htmlFor='manual-discount-all' className='text-sm cursor-pointer'>
                                            Alle Positionen
                                        </Label>
                                    </div>
                                    {!manualDiscountTargetAll && currentLineItems.length > 0 && (
                                        <div className='ml-6 space-y-1'>
                                            {currentLineItems.map((lineItem, idx) => (
                                                <div key={idx} className='flex items-center space-x-2'>
                                                    <Checkbox
                                                        id={`manual-target-${idx}`}
                                                        checked={manualDiscountTargetIndices.includes(idx)}
                                                        onCheckedChange={(checked) => {
                                                            setManualDiscountTargetIndices(prev =>
                                                                checked
                                                                    ? [...prev, idx]
                                                                    : prev.filter(i => i !== idx)
                                                            );
                                                        }}
                                                    />
                                                    <Label htmlFor={`manual-target-${idx}`} className='text-xs cursor-pointer'>
                                                        {idx + 1}. {lineItem.description}
                                                    </Label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>}

                {/* Totals Summary */}
                <Card>
                    <CardHeader className='pb-3'>
                        <CardTitle className='text-base'>Zusammenfassung</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className='flex flex-col items-end space-y-2'>
                            {totalDiscount > 0 && (
                                <>
                                    <div className='flex justify-between w-64 text-sm'>
                                        <span className='text-muted-foreground'>Bruttobetrag:</span>
                                        <span className='font-mono'>{formatCurrency(totals.totalGross)}</span>
                                    </div>
                                    <div className='flex justify-between w-64 text-sm'>
                                        <span className='text-green-600 font-medium'>Rabatt gesamt:</span>
                                        <span className='font-mono text-green-600'>-{formatCurrency(totalDiscount)}</span>
                                    </div>
                                </>
                            )}
                            <div className='flex justify-between w-64 text-sm'>
                                <span className='text-muted-foreground'>Nettobetrag:</span>
                                <span className='font-mono'>{formatCurrency(totals.totalNetDiscounted)}</span>
                            </div>
                            <div className='flex justify-between w-64 text-sm'>
                                <span className='text-muted-foreground'>MwSt.:</span>
                                <span className='font-mono'>{formatCurrency(totals.vatAmountDiscounted)}</span>
                            </div>
                            <Separator className='w-64' />
                            <div className='flex justify-between w-64 text-lg font-bold'>
                                <span>Gesamtbetrag:</span>
                                <span className='font-mono'>{formatCurrency(totals.totalGrossDiscounted)}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                </div>
            )}
            {/* Discount code warning override dialog */}
            <AlertDialog open={discountWarningDialogOpen} onOpenChange={setDiscountWarningDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Rabattcode-Einschränkung</AlertDialogTitle>
                        <AlertDialogDescription className='whitespace-pre-line'>
                            {discountWarningMessage}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => {
                            setPendingDiscountCodeId(null);
                            setDiscountWarningDialogOpen(false);
                            setDiscountWarningMessage('');
                        }}>
                            Abbrechen
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={() => {
                            if (pendingDiscountCodeId) {
                                applyDiscountCodeDirectly(pendingDiscountCodeId, true);
                            }
                            setPendingDiscountCodeId(null);
                            setDiscountWarningDialogOpen(false);
                            setDiscountWarningMessage('');
                        }}>
                            Trotzdem anwenden
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default BillingDraftLineItemsEditor;
